import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { useMedia } from './useMedia';

type Offset = { left: number; top: number };

type Snapshot = {
  itemKeys: string[];
  offsets: Map<string, Offset>;
};

type ShiftArgs = {
  item: Element;
  from: Offset;
  to: Offset;
  duration: number;
  easing: string;
};

export type UseFlipReorderArgs = {
  containerRef: RefObject<HTMLElement | null>;
  itemKeys: string[];
  duration?: number;
  easing?: string;
};

const KEY_SEPARATOR = '\u0000';
const DEFAULT_DURATION_MS = 350;
const DEFAULT_EASING = 'ease-out';

const toItemKeys = (orderKey: string): string[] =>
  orderKey === '' ? [] : orderKey.split(KEY_SEPARATOR);

const toOffset = (item: Element, containerRect: DOMRect): Offset => {
  const { left, top } = item.getBoundingClientRect();
  return { left: left - containerRect.left, top: top - containerRect.top };
};

const measureOffsets = (
  container: HTMLElement,
  itemKeys: string[]
): Map<string, Offset> => {
  const containerRect = container.getBoundingClientRect();
  return new Map(
    itemKeys.flatMap((key, index): [string, Offset][] => {
      const item = container.children[index];
      if (!item) return [];
      return [[key, toOffset(item, containerRect)]];
    })
  );
};

const hasReordered = (previous: string[], current: string[]): boolean =>
  previous.length !== current.length ||
  previous.some((key, index) => key !== current[index]);

const hasMoved = (from: Offset, to: Offset): boolean =>
  from.left !== to.left || from.top !== to.top;

const isRunning = ({ playState }: Animation): boolean =>
  playState === 'running';

const shiftItem = ({
  item,
  from,
  to,
  duration,
  easing,
}: ShiftArgs): Animation =>
  item.animate(
    [
      {
        transform: `translate(${from.left - to.left}px, ${from.top - to.top}px)`,
      },
      { transform: 'translate(0px, 0px)' },
    ],
    { duration, easing }
  );

/**
 * FLIP animation for a list whose items change position.
 * `itemKeys` must match the container's direct children, in order.
 * New and removed items are not animated, only the ones that shift around them.
 */
export const useFlipReorder = ({
  containerRef,
  itemKeys,
  duration = DEFAULT_DURATION_MS,
  easing = DEFAULT_EASING,
}: UseFlipReorderArgs): void => {
  const prefersReducedMotion = useMedia('(prefers-reduced-motion: reduce)');
  const snapshotRef = useRef<Snapshot | null>(null);
  const animationsRef = useRef<Animation[]>([]);
  const orderKey = itemKeys.join(KEY_SEPARATOR);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    animationsRef.current.forEach(animation => animation.cancel());
    animationsRef.current = [];
    const currentKeys = toItemKeys(orderKey);
    const offsets = measureOffsets(container, currentKeys);
    const previous = snapshotRef.current;
    // jsdom has no web animations api
    const canAnimate = typeof container.animate === 'function';
    if (
      canAnimate &&
      previous &&
      !prefersReducedMotion &&
      hasReordered(previous.itemKeys, currentKeys)
    ) {
      animationsRef.current = currentKeys.flatMap((key, index) => {
        const item = container.children[index];
        const from = previous.offsets.get(key);
        const to = offsets.get(key);
        if (!item || !from || !to || !hasMoved(from, to)) return [];
        return [shiftItem({ item, from, to, duration, easing })];
      });
    }
    snapshotRef.current = { itemKeys: currentKeys, offsets };
  }, [containerRef, orderKey, prefersReducedMotion, duration, easing]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const refreshSnapshot = (): void => {
      const snapshot = snapshotRef.current;
      if (!snapshot || animationsRef.current.some(isRunning)) return;
      snapshotRef.current = {
        ...snapshot,
        offsets: measureOffsets(container, snapshot.itemKeys),
      };
    };
    const resizeObserver = new ResizeObserver(refreshSnapshot);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [containerRef]);
};
